import React from 'react';

interface InvoicePrintViewProps {
  invoice: any;
  project: any;
  user: any;
}

const InvoicePrintView: React.FC<InvoicePrintViewProps> = ({ invoice, project, user }) => {
  const subtotal = invoice.subtotal || (invoice.items?.reduce((acc: number, item: any) => acc + (item.quantity * item.unit_price), 0)) || 0;
  
  return (
    <div className="bg-white p-6 md:p-12 w-full max-w-[210mm] mx-auto shadow-none print:shadow-none print:m-0 print:p-8 print:w-full print:max-w-none text-sm" id="invoice-print">
      {/* Header */}
      <div className="flex justify-between items-start mb-8 print:mb-6">
        <div>
          <h1 className="text-4xl font-black text-gray-900 uppercase tracking-tighter mb-2">Invoice</h1>
          <div className="inline-flex flex-col">
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Invoice Number</span>
            <span className="text-xs font-mono text-gray-900 bg-gray-50 print:bg-white px-3 py-1.5 rounded-lg border border-gray-100 print:border-none print:p-0">#{invoice.invoice_number}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="w-12 h-12 bg-black print:bg-gray-800 rounded-xl flex items-center justify-center mb-2 ml-auto print:border print:border-gray-300">
            <span className="text-white font-black text-xl">{user.company_name?.charAt(0) || user.first_name?.charAt(0)}</span>
          </div>
          {user.company_name ? (
            <h2 className="text-lg font-black text-gray-900">{user.company_name}</h2>
          ) : (
            <h2 className="text-lg font-black text-gray-900">{user.first_name} {user.last_name}</h2>
          )}
          <p className="text-xs text-gray-500 font-medium">{user.email}</p>
          {user.gst_number && (
            <div className="mt-1 inline-flex items-center space-x-2 bg-teal-50 print:bg-white px-2 py-1 rounded-full print:rounded-none print:border print:border-gray-200">
              <span className="text-[8px] font-black uppercase tracking-widest text-teal-600 print:text-gray-600">GSTIN</span>
              <span className="text-[10px] font-black text-teal-700 print:text-gray-900">{user.gst_number}</span>
            </div>
          )}
        </div>
      </div>

      <div className="h-px bg-gray-100 print:bg-gray-300 w-full mb-8 print:mb-6" />

      {/* Bill To & Details */}
      <div className="grid grid-cols-2 gap-8 print:gap-12 mb-8 print:mb-6">
        <div className="bg-gray-50 print:bg-white p-6 rounded-[1.5rem] print:p-0 print:rounded-none">
          <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Bill To</h3>
          <div className="space-y-1">
            <p className="text-lg font-black text-gray-900">{project.client_name}</p>
            <p className="text-xs text-gray-500 font-medium">{project.client_email}</p>
          </div>
        </div>
        <div className="flex flex-col justify-center">
          <div className="space-y-2">
            <div className="flex justify-between items-center py-1 border-b border-gray-50 print:border-gray-200">
                <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px]">Date Issued</span>
                <span className="font-bold text-gray-900 text-xs">{new Date(invoice.created_at).toLocaleDateString(undefined, { dateStyle: 'long' })}</span>
            </div>
            <div className="flex justify-between items-center py-1 border-b border-gray-50 print:border-gray-200">
                <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px]">Due Date</span>
                <span className="font-bold text-gray-900 text-red-500 text-xs">{new Date(invoice.due_date).toLocaleDateString(undefined, { dateStyle: 'long' })}</span>
            </div>
            <div className="flex justify-between items-center py-1 border-b border-gray-50 print:border-gray-200">
                <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px]">Project</span>
                <span className="font-bold text-gray-900 text-xs truncate max-w-[150px] text-right">{project.title}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div className="mb-8 print:mb-6">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="text-left py-2 text-[9px] font-black uppercase tracking-widest text-gray-400 border-b-2 border-gray-900 print:border-gray-400">Description</th>
              <th className="text-center py-2 text-[9px] font-black uppercase tracking-widest text-gray-400 border-b-2 border-gray-900 print:border-gray-400 w-16">Qty</th>
              <th className="text-right py-2 text-[9px] font-black uppercase tracking-widest text-gray-400 border-b-2 border-gray-900 print:border-gray-400 w-24">Unit Price</th>
              <th className="text-right py-2 text-[9px] font-black uppercase tracking-widest text-gray-400 border-b-2 border-gray-900 print:border-gray-400 w-24">Total</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items?.map((item: any, index: number) => (
              <tr key={index}>
                <td className="py-3 text-xs font-bold text-gray-900 border-b border-gray-50 print:border-gray-200">{item.description}</td>
                <td className="py-3 text-center text-xs font-bold text-gray-500 border-b border-gray-50 print:border-gray-200">{item.quantity}</td>
                <td className="py-3 text-right text-xs font-bold text-gray-500 border-b border-gray-50 print:border-gray-200">₹{item.unit_price.toLocaleString()}</td>
                <td className="py-3 text-right text-sm font-black text-gray-900 border-b border-gray-50 print:border-gray-200">₹{(item.quantity * item.unit_price).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="flex justify-end mb-16 print:mb-12 page-break-inside-avoid">
        <div className="w-64 space-y-2">
          <div className="flex justify-between items-center text-xs border-b border-gray-50 print:border-gray-200 pb-1">
            <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px]">Subtotal</span>
            <span className="font-bold text-gray-900">₹{subtotal.toLocaleString()}</span>
          </div>
          {(invoice.discount_amount > 0) && (
            <div className="flex justify-between items-center text-xs border-b border-gray-50 print:border-gray-200 pb-1">
              <span className="text-emerald-500 print:text-gray-600 font-bold uppercase tracking-widest text-[9px]">Discount</span>
              <span className="font-bold text-emerald-500 print:text-gray-900">-₹{invoice.discount_amount.toLocaleString()}</span>
            </div>
          )}
          {invoice.tax > 0 && (
             <div className="flex justify-between items-center text-xs border-b border-gray-50 print:border-gray-200 pb-1">
              <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px]">GST ({((invoice.tax / subtotal) * 100).toFixed(0)}%)</span>
              <span className="font-bold text-gray-900">₹{invoice.tax.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t border-gray-900 items-end">
            <span className="text-[10px] font-black uppercase tracking-widest">Total Due</span>
            <span className="text-2xl font-black text-gray-900 leading-none tracking-tighter">₹{invoice.total_amount.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Terms & Footer */}
      <div className="mt-8 border-t border-gray-100 print:border-gray-300 pt-6 page-break-inside-avoid">
        <div className="grid grid-cols-2 gap-8 items-end">
          <div>
            <h4 className="text-[9px] font-black uppercase tracking-widest text-gray-400 mb-2">Terms & Notes</h4>
            <p className="text-[10px] text-gray-500 leading-relaxed italic">{invoice.notes || "Please make payment within the due date. Thank you for choosing our services!"}</p>
          </div>
          <div className="text-right">
            <div className="inline-block bg-gray-50 print:bg-white print:border print:border-gray-200 p-4 rounded-xl print:rounded-none">
               <p className="text-[7px] font-black uppercase tracking-widest text-gray-400 mb-1">Authenticated By</p>
               <div className="flex items-center justify-end space-x-1.5">
                 <span className="text-[10px] font-black text-gray-900">Click</span>
                 <span className="text-[10px] font-black text-teal-500 print:text-gray-900">Collab</span>
                 <div className="w-1 h-1 rounded-full bg-teal-500 print:bg-gray-400" />
                 <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Official CRM</span>
               </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePrintView;
